import {
  type Cover,
  CUT_IN_TTL_MS,
  CUT_SHORT_MS,
  NUDGE_MS,
  PAUSED_PROMPT_MS,
  OPENING_TTL_MS,
  SAY_INTENT_TTL_MS,
  SAY_WAIT_MS,
  STOP_MS,
  WORKING_MS,
  MAX_HOLD_MS,
  WORK_PAUSED_MS,
  DEFAULT_ASKING,
  STEP_ASK_WITHIN_MS,
  STEP_HOLD_MS,
  STEP_NEW_PAGE_MS,
  type AskingMode,
  type QuestionId,
  type QuestionSeed,
  type RemovedPart,
  type Request,
  type SessionEvent,
  type SessionEventType,
  type TapeEntry,
} from "./events";

/**
 * An interview session (decision 111, ENG-1014) as a pure fold over its tape: the ordered events
 * in `./events` go in, the session comes out. It holds every rule of the interview that needs no
 * model; model judgments arrive as events like everything else, so a tape replays to the same
 * session in the API after a restart, in a test and in the island. It imports nothing at run time
 * and never changes what it is given.
 *
 * The rules it keeps:
 *
 * - **Questions are a pool.** A question is `open` until Abacor asks it (`asked`, its card
 *   showing, one at a time). Talked past or "not now" once, it is `passed` and comes back at most
 *   once at a later break; a second time, it is `held` for the chat. `answered` and `closed` take
 *   it out of the pool until Undo or Reopen puts it back.
 * - **Any input can answer any question.** The person's words, typing and screen actions are kept
 *   as inputs; a match judgment then says whether one fits one question (applied, with a receipt
 *   that can be reversed), two (Abacor asks which with a clarifier) or none (teaching: a note in
 *   the rough understanding). A question answered after it was asked, or that the person came back
 *   to, is `answered` with Undo; one never asked that the person's speech or screen actions
 *   covered along the way is `closed` by Abacor, with Reopen. A tap on a likely answer, or typing into a question's own field,
 *   answers that question directly. An input the person gave after a question was asked that does
 *   not fit it talks past it.
 * - **Asking.** At a break Abacor asks an "about now" question first, then the newest open one,
 *   then the one retry (the longest-waiting passed question). It never asks while a question
 *   shows, while the person types, speaks or reads what Abacor has got, while a clarifier is open,
 *   or while it cannot speak. The open questions list does not hold it back: the person is looking
 *   at the questions, and the one asked takes the list's place. It may cut in only with an "about now" question, after a cue, when a cut-in
 *   judgment says now is the moment; the question it displaces goes back as it was, uncounted.
 * - **Replies.** When the person speaks to Abacor itself, the match judgment says what they ask
 *   (`Request`), beside what the input answers or teaches. Abacor replies once, short: about the
 *   session from what the session knows, what was said from the notes, a recap with its view, the
 *   last line again, what the question showing is after, or a moment's hold. It does not judge the
 *   person's work: it says so kindly and keeps their doubt as a note for the chat. A question asked
 *   at the break after the input gives way to the reply. Speech meant for someone else is an aside:
 *   no reply, no note, no evidence. Asked twice what a question means, Abacor leaves it for the
 *   chat.
 * - **Abacor's words.** Everything Abacor should say is a say intent: the line on the transcript
 *   made by one tape entry (its `seq` is the intent's ID) with an expiry. The voice worker speaks it
 *   only while it is `say`, the current intent, and reports `started`, `finished` or `cut-off` (a
 *   barge-in, which the person always wins); a session without voice shows the same line as text.
 *   Abacor does not cut itself off: a line it makes while another is being said waits for it.
 *   A question gives way: when the person speaks before it begins, it is not said, and one they cut
 *   off before it played `CUT_SHORT_MS` was not heard. Either goes back unpassed and is asked first
 *   at the next pause (a cut-off one, once); one the person picked stays asked, to be answered.
 *   An intent not started by its expiry expires. While voice is offline or a call has the mic,
 *   Abacor says nothing and questions wait; typing silences it too. Its words are never evidence:
 *   a judgment must name one of the person's inputs.
 * - **Stale judgments.** Every judgment names the tape position it was computed against
 *   (`basedOn`). If the pool changed after that (a question raised, answered, closed, undone or
 *   reopened), the result is dropped as `stale` so the caller can run it again. Asking, passing and
 *   holding do not change what an input could answer, so they do not make a judgment stale.
 * - **Corrections** make the latest statement win in the rough understanding; both notes stay,
 *   linked both ways, and Undo restores the earlier one. A correction of a note that has been
 *   corrected or undone since is stale too.
 * - **Pause captures nothing**: the person's inputs and screen events are dropped while paused,
 *   and Abacor says nothing but the finish prompt (judgments of earlier inputs still apply).
 *   After 15 minutes paused Abacor asks whether to finish. Limits count recorded (live) time: a
 *   nudge at 30 minutes, a stop at 60. A `timer` entry only wakes the session; limits are worked
 *   out from each entry's time, so a timer that fires early or twice changes nothing.
 * - **Finish** (or the stop) holds every open, asked or passed question for the chat; `handoff`
 *   lists every held question. Answer now goes back in and asks the first held one.
 * - **Amendments** (ENG-1029) come once the session has ended, from the package view, and never
 *   edit what was captured. A note at a moment is the person's own words, kept beside what they
 *   said. A corrected answer is added to its question: the earlier one stays, linked both ways, and
 *   the newer one stands (a question that had none is answered). A removal takes out a line said or
 *   typed, a note, a screen or an action, or everything in a stretch of the clock: each is marked
 *   removed, and what Abacor took from it goes too (its notes are withdrawn, its answers drop out,
 *   and the answer before a dropped one stands again, or the question is held for the chat). Every
 *   removal is logged with what it dropped. Before the end they are dropped as `not-ended`.
 * - **The tape.** An entry at or before `lastSeq` was already applied and changes nothing (a
 *   replay that resumes early); a second entry with an idempotency key already seen is dropped as
 *   a duplicate. Every entry that changes nothing for a reason is listed in `dropped`.
 */

export type QuestionState = "open" | "asked" | "passed" | "held" | "answered" | "closed";
export type Channel = "voice" | "typed" | "tapped" | "screen";

export type Question = QuestionSeed & {
  state: QuestionState;
  /** Times it was talked past or put off. One retry, then it is held for the chat. */
  passes: number;
  /** Times the person asked what it means; the second time it is left for the chat. */
  unclear: number;
  /** The entry that put it in the pool. */
  raisedAt: number;
  /** The entry that last asked it, so words heard after it can show under it. */
  askedAt: number | null;
  answer: {
    /** The input that answered it. */
    input: number;
    words: string;
    via: Channel;
    /** What Abacor took from it. */
    took: string;
    /** The receipt that can reverse it. */
    receipt: number;
  } | null;
  /**
   * Every answer it was given, in order, the current one last when it stands (ENG-1028). One that
   * was undone or reopened stays here, marked, so the package shows what changed.
   */
  answers: AnswerRecord[];
};

/** One answer a question was given, and whether it was later undone or reopened. */
export type AnswerRecord = NonNullable<Question["answer"]> & {
  /** When the entry that gave it landed. */
  at: number;
  undone: boolean;
  /** A correction (ENG-1029): the moment on the clock it was given for, after the end. */
  moment?: number;
  /** The answer it corrected, and the one that corrected it, by their receipts. */
  corrects?: number;
  correctedBy?: number;
  /** Its input was taken out, so what Abacor took from it no longer counts. */
  removed?: true;
};

export const SAY_KINDS = [
  "opening",
  "ask",
  "retry",
  "cut-in",
  "clarify",
  "receipt",
  "reply",
  "nudge",
  "finish-prompt",
  "stopped",
] as const;
export type SayKind = (typeof SAY_KINDS)[number];

/**
 * `replaced`: a newer intent took its place before it started. `canceled`: what it was about went
 * away (answered, passed, typing, pause) before it started.
 */
export type SayOutcome =
  | "pending"
  | "started"
  | "finished"
  | "cut-off"
  | "expired"
  | "replaced"
  | "canceled";

export type PersonLine = {
  seq: number;
  at: number;
  who: "person";
  via: Exclude<Channel, "screen">;
  text: string;
  /** Meant for someone else in the room, not Abacor: kept, but never a note or an answer. */
  aside?: true;
  /** Taken out after the end (ENG-1029): kept on the tape, out of everything shown. */
  removed?: true;
};

/** A say intent and what became of it. */
export type SystemLine = {
  seq: number;
  at: number;
  who: "system";
  kind: SayKind;
  text: string;
  /** Said first, before a cut-in. */
  cue: string | null;
  questionId: QuestionId | null;
  expiresAt: number;
  outcome: SayOutcome;
};

export type TranscriptLine = PersonLine | SystemLine;

export type ScreenAction = {
  seq: number;
  at: number;
  text: string;
  keyframe: string | null;
  /** Taken out after the end (ENG-1029). */
  removed?: true;
};

/**
 * A note the person added at a moment of an ended recording (ENG-1029): their own words, placed at
 * `at` on the recording's clock, written at `writtenAt`.
 */
export type NotedLine = {
  seq: number;
  at: number;
  text: string;
  writtenAt: number;
  removed: boolean;
};

/** A removal (ENG-1029) and what it took out: the notes it withdrew and the answers it dropped. */
export type Removal = {
  seq: number;
  at: number;
  part: RemovedPart;
  dropped: {
    notes: number[];
    answers: { questionId: QuestionId; receipt: number }[];
  };
};

/**
 * A stretch the person spent in something not shared with Abacor (ENG-1031), named by `app`. Open
 * (`to: null`) until they come back, the recording pauses or it ends.
 */
export type UnsharedSpan = {
  seq: number;
  app: string;
  from: number;
  to: number | null;
  /** Abacor's own app. */
  own?: true;
};

/** A line of the rough understanding, made by the entry `seq` from the person's input `input`. */
export type Note = {
  seq: number;
  input: number;
  topic: string;
  text: string;
  /** The needs of a process it covers, as the match judgment guessed them; none on older tapes. */
  covers: Cover[];
  /** The note this one corrected, and the one that corrected this. */
  supersedes: number | null;
  supersededBy: number | null;
  /** Undone, or its input removed: kept in the record, out of the rough understanding. */
  withdrawn: boolean;
};

export type Receipt = {
  seq: number;
  at: number;
  kind: "understood" | "changed" | "answered" | "closed" | "held" | "system";
  title: string;
  detail: string | null;
  action: "undo" | "reopen" | null;
  questionId: QuestionId | null;
  note: number | null;
  reversed: boolean;
};

export type LedgerWhy =
  | "seeded"
  | "raised"
  | "asked"
  | "retried"
  | "picked"
  | "cut-in"
  | "answer-now"
  | "talked-past"
  | "not-now"
  | "picked-another"
  | "unclear"
  | "replaced"
  | "gave-way"
  | "cut-short"
  | "answered"
  | "closed"
  | "undone"
  | "reopened"
  | "finished"
  | "stopped"
  | "corrected"
  | "removed";

/** One change of one question's state: the question ledger the recording package keeps. */
export type LedgerEntry = {
  seq: number;
  questionId: QuestionId;
  state: QuestionState;
  why: LedgerWhy;
};

export type DropReason =
  | "duplicate"
  | "not-started"
  | "ended"
  | "paused"
  | "mic-off"
  | "on-call"
  | "stale"
  | "not-person"
  | "unknown-question"
  | "not-answerable"
  | "not-current"
  | "expired"
  | "busy"
  | "not-ended"
  | "invalid";

export type Dropped = { seq: number; type: SessionEventType; why: DropReason };

export type Session = {
  mode: "new" | "live" | "paused" | "finished" | "stopped";
  intent: string | null;
  /** `none`: a session that asks in text only. `offline`: the voice channel dropped. */
  voice: "none" | "live" | "offline";
  voiceMuted: boolean;
  micOn: boolean;
  callOnMic: boolean;
  masked: boolean;
  typing: boolean;
  personSpeaking: boolean;
  view: "understanding" | "open" | null;
  questions: Question[];
  /** The question whose card is showing. */
  asked: QuestionId | null;
  /** Abacor is asking which of two questions the input `input` was about. */
  clarifier: {
    seq: number;
    input: number;
    /** What Abacor asks, kept here because it may not be able to say it. */
    text: string;
    fits: { questionId: QuestionId; took: string }[];
  } | null;
  /** The current say intent, by its line's `seq`. */
  say: number | null;
  /**
   * A question the person never got to hear (they spoke before it began, or cut it off as it
   * began): asked first at the next pause.
   */
  again: QuestionId | null;
  /** A line Abacor made while another was being said: it becomes `say` when that one ends. */
  next: number | null;
  /** The line current when Abacor was muted: the worker stopped it and never says it again. */
  stoppedByMute: number | null;
  /** The person cut Abacor off; cleared by the next thing it says. */
  cutOff: boolean;
  /**
   * The person asked for a moment, by the entry that held: no questions until something they say
   * or type after it is judged to carry on (not an aside), and they have paused.
   */
  holding: number | null;
  /** The last natural pause, by its entry; a hold that ends after it asks as that pause would. */
  lastBreak: number | null;
  /**
   * A pause Abacor did not ask at because the person was still working on screen, by its entry:
   * it asks once they have done nothing on screen for `WORKING_MS`, at the timer that wakes then.
   */
  waiting: number | null;
  /** When the first pause of the current hold came; past `MAX_HOLD_MS` Abacor asks anyway. */
  waitingSince: number | null;
  /**
   * When the person last did something on screen: a screen kept for what they did or from just
   * before a click, not one kept because they started talking or for nothing happening.
   */
  workedAt: number | null;
  /** How Abacor asks what can wait, chosen at Start (`ASKING_MODES`). */
  asking: AskingMode;
  /** When the person last moved to each page they have been on, by what capture called it. */
  pages: Record<string, number>;
  /** The last end of a step: a move to a page not visited for `STEP_NEW_PAGE_MS`, by its entry. */
  stepEnded: { seq: number; at: number } | null;
  notes: Note[];
  receipts: Receipt[];
  /** Everything said, both voices: the person's inputs and Abacor's say intents. */
  transcript: TranscriptLine[];
  screen: ScreenAction[];
  /** Time spent in what is not shared, oldest first. */
  unshared: UnsharedSpan[];
  /** Notes the person added after the end, in the order they were written (ENG-1029). */
  noted: NotedLine[];
  /** Every removal after the end, in order (ENG-1029). */
  removals: Removal[];
  ledger: LedgerEntry[];
  dropped: Dropped[];
  /**
   * When the person pressed Start, after choosing what Abacor sees (ENG-1031); null for a recording
   * that started when it was made. Recorded time counts from it.
   */
  startedAt: number | null;
  /** When the session started (its first entry), where the clock starts without a Start. */
  firstAt: number | null;
  /** Recorded time before `liveSince`; while live, add the time since. */
  liveMs: number;
  liveSince: number | null;
  pausedAt: number | null;
  finishPrompted: boolean;
  nudged: boolean;
  endedAt: number | null;
  /**
   * Every time the session ended (Finish, or the hour limit), oldest first, with the entry that
   * ended it (ENG-1028). One reopened by Answer now ends again, so there can be several.
   */
  ended: { seq: number; at: number }[];
  /** The last entry that changed the pool; a judgment based on an earlier one is stale. */
  poolChangedAt: number;
  /** Set at Finish or the stop: every question held for the chat. */
  handoff: { held: QuestionId[] } | null;
  lastSeq: number;
  /** Idempotency keys applied, with the entry each came in. */
  keys: Record<string, number>;
};

export const COPY = {
  // Warm, and says what the recording is: the person shows their work and Abacor asks as it goes.
  // With voice, the mic starts off: turning it on to talk the work through is the way in, typing
  // the way round. It names the island's buttons, not keys, which differ by system.
  opening:
    "Hi! Show me how you do it, and type a note when something isn't obvious. I'll ask questions as we go. Pause any time. What are we looking at?",
  // Short, since people start talking within seconds of Start (3 November 2026: the longer one, 14
  // seconds, was talked over in four of six recordings, and still said the mic was off after they
  // had turned it on). Typing, muting and pausing are on the island's buttons.
  openingVoice:
    "Hi! Turn your mic on and talk me through your work as you do it. I'll ask as we go. What are we looking at?",
  openingVoiceMicOn:
    "Hi! Talk me through your work as you do it, and I'll ask as we go. What are we looking at?",
  retry: (text: string) => `Coming back to this: ${text}`,
  cue: "Sorry, quick one.",
  later: "Sure, later.",
  gotIt: "Got it.",
  nudge: "We're at half an hour. Want to wrap this part up? The rest can be its own recording.",
  finishPrompt: "You've been paused for a while. Should I end this recording?",
  stopped: "That's an hour, so I've stopped this recording. Keep going and it starts a new one.",
  held: "I'll bring this to the chat",
  voiceOffline: "Abacor's voice is offline",
  voiceOfflineDetail: "Keep going. Still capturing; questions come to the chat after.",
  voiceBack: "Abacor is back",
  nudgeReceipt: "Half an hour in",
  // Replies to the person speaking to Abacor itself: short, and never an answer of Abacor's own.
  judge:
    "Good question. Right now I'm just learning how you do it today. We can talk through what's best in the chat after. How would you normally do it?",
  doubtTopic: "To discuss",
  hold: "Take your time.",
  unclear: "Let's leave that one. I'll bring it to the chat.",
  recapNone: "Nothing yet. Keep going, and I'll note things as we go.",
  recap: (count: number) =>
    `${count === 1 ? "One thing" : `${String(count)} things`} so far. They're up now. Tell me anything I got wrong.`,
  seeing: "Yes, I can see what you're sharing.",
  seeingNothing: "Not yet. Nothing has come through from your screen so far.",
  seeingMasked: "That field is masked, so I'm leaving it out.",
  seeingUnshared: (app: string) => `You're in ${app}, which you haven't shared with me.`,
  seeingOwn: "You're in Abacor itself, which I leave out. Go back to your work and I'll see it.",
  hearing: "Yes, I can hear you.",
  hearingOff: "Your mic is off, so I can't hear you. Click the mic to turn it on.",
  next: "I'll keep asking as we go. When you finish, what we covered goes to the chat, and anything still open waits there.",
} as const;

export function newSession(): Session {
  return {
    mode: "new",
    intent: null,
    voice: "none",
    voiceMuted: false,
    micOn: true,
    callOnMic: false,
    masked: false,
    typing: false,
    personSpeaking: false,
    view: null,
    questions: [],
    asked: null,
    clarifier: null,
    say: null,
    again: null,
    next: null,
    stoppedByMute: null,
    cutOff: false,
    holding: null,
    lastBreak: null,
    waiting: null,
    waitingSince: null,
    workedAt: null,
    asking: DEFAULT_ASKING,
    pages: {},
    stepEnded: null,
    notes: [],
    receipts: [],
    transcript: [],
    screen: [],
    unshared: [],
    noted: [],
    removals: [],
    ledger: [],
    dropped: [],
    startedAt: null,
    firstAt: null,
    liveMs: 0,
    liveSince: null,
    pausedAt: null,
    finishPrompted: false,
    nudged: false,
    endedAt: null,
    ended: [],
    poolChangedAt: 0,
    handoff: null,
    lastSeq: 0,
    keys: {},
  };
}

/** The session with one tape entry applied. */
export function apply(session: Session, entry: TapeEntry): Session {
  if (entry.seq <= session.lastSeq) return session;
  const draft = structuredClone(session);
  step(draft, entry);
  return draft;
}

/** The session a whole tape gives, from the start. */
export function replay(entries: readonly TapeEntry[], from: Session = newSession()): Session {
  const draft = structuredClone(from);
  for (const entry of entries) if (entry.seq > draft.lastSeq) step(draft, entry);
  return draft;
}

/* ------------------------------------------------------------------ */
/* Views of a session                                                  */

/** Still in the pool: what the open count counts and the open list lists. */
export const isAnswerable = (q: Question) =>
  q.state === "open" || q.state === "asked" || q.state === "passed" || q.state === "held";

export const openQuestions = (s: Session) => s.questions.filter(isAnswerable);

/**
 * Whether the session is at a moment Abacor could ask with nothing in the pool to ask, at `at`:
 * live, free to speak, nothing showing, no open or passed question, and the person has done
 * nothing on screen for `WORK_PAUSED_MS`. A break that finds this is where the pool may be filled
 * from what the process still needs.
 */
export const wantsQuestion = (s: Session, at: number) =>
  canAsk(s) &&
  !s.questions.some((q) => q.state === "open" || q.state === "passed") &&
  fillsAt(s, at);

/** Whether a pause at `at` is where the asking mode fills the pool (`ASKING_MODES`). */
function fillsAt(s: Session, at: number): boolean {
  switch (s.asking) {
    case "speech-pause":
      return true;
    case "step-end":
      return s.stepEnded !== null && at - s.stepEnded.at <= STEP_ASK_WITHIN_MS;
    case "work-pause":
      return s.workedAt === null || at - s.workedAt >= WORK_PAUSED_MS;
  }
}

/**
 * The rough understanding: the person's notes that stand (corrected or undone ones are left out)
 * and what Abacor took from each answer, in the order they came. Never steps and never numbered.
 */
export function understanding(
  s: Session,
): { text: string; topic: string | null; source: number }[] {
  const notes = s.notes
    .filter((n) => n.supersededBy === null && !n.withdrawn)
    .map((n) => ({ text: n.text, topic: n.topic, source: n.seq }));
  const answers = s.questions.flatMap((q) =>
    (q.state === "answered" || q.state === "closed") && q.answer
      ? [{ text: q.answer.took, topic: null, source: q.answer.receipt }]
      : [],
  );
  return [...notes, ...answers].sort((a, b) => a.source - b.source);
}

/** How long a receipt shows before the island shrinks back. */
export const RECEIPT_SHOW_MS = 5_000;

export type Showing =
  | { kind: "new" }
  | { kind: "ended"; mode: "finished" | "stopped"; held: Question[] }
  | { kind: "paused"; askFinish: boolean }
  | { kind: "call" }
  | { kind: "masked" }
  | { kind: "view"; view: "understanding" | "open" }
  | { kind: "clarifier"; text: string; fits: Question[] }
  | { kind: "question"; question: Question; heard: PersonLine[] }
  | { kind: "receipt"; receipt: Receipt }
  | { kind: "idle" };

/**
 * The one thing the island grows for at time `now`, or `idle` for its slim row alone. A view the
 * person opened is a look, so it shows while the recording is paused or ended too, over the
 * paused row or the finish summary, and Close goes back to them; pausing or ending closes it.
 */
export function showing(s: Session, now: number): Showing {
  if (s.mode === "new") return { kind: "new" };
  if (s.mode === "finished" || s.mode === "stopped") {
    if (s.view) return { kind: "view", view: s.view };
    const held = s.questions.filter((q) => q.state === "held");
    return { kind: "ended", mode: s.mode, held };
  }
  if (s.mode === "paused") {
    return s.view
      ? { kind: "view", view: s.view }
      : { kind: "paused", askFinish: s.finishPrompted };
  }
  if (s.callOnMic) return { kind: "call" };
  if (s.masked) return { kind: "masked" };
  if (s.view) return { kind: "view", view: s.view };
  if (s.clarifier) {
    const fits = s.clarifier.fits.flatMap(({ questionId }) =>
      s.questions.filter((q) => q.id === questionId),
    );
    return { kind: "clarifier", text: s.clarifier.text, fits };
  }
  const asked = s.questions.find((q) => q.id === s.asked);
  if (asked) {
    // A new question starts a new exchange: what the person said before it is not shown with it.
    const heard = s.transcript.filter(
      (l): l is PersonLine => l.who === "person" && l.seq > (asked.askedAt ?? 0),
    );
    return { kind: "question", question: asked, heard };
  }
  const receipt = s.receipts.at(-1);
  if (receipt && !receipt.reversed && now - receipt.at < RECEIPT_SHOW_MS)
    return { kind: "receipt", receipt };
  return { kind: "idle" };
}

/** Recorded time at `now`, paused time left out. */
export const recordedMs = (s: Session, now: number) =>
  s.liveMs + (s.liveSince === null ? 0 : Math.max(0, now - s.liveSince));

/** When the session next needs a `timer` entry to reach a limit, or null when none is due. */
export function dueAt(s: Session): number | null {
  if (s.mode === "live" && s.liveSince !== null) {
    const limit = s.nudged ? STOP_MS : NUDGE_MS;
    const stop = s.liveSince + limit - s.liveMs;
    const worked = s.screen.at(-1)?.at;
    if (s.asking === "step-end")
      return s.waiting !== null && s.waitingSince !== null
        ? Math.min(stop, s.waitingSince + STEP_HOLD_MS)
        : stop;
    return s.waiting !== null && worked !== undefined
      ? Math.min(stop, worked + WORKING_MS, (s.waitingSince ?? worked) + MAX_HOLD_MS)
      : stop;
  }
  if (s.mode === "paused" && s.pausedAt !== null && !s.finishPrompted)
    return s.pausedAt + PAUSED_PROMPT_MS;
  return null;
}

/* ------------------------------------------------------------------ */
/* The transition                                                      */

/** Events still taken while paused: everything that is not the person's input or Abacor asking. */
const WHILE_PAUSED: ReadonlySet<SessionEventType> = new Set([
  "person.resume",
  "person.finish",
  "person.view",
  "person.mic",
  "person.voice",
  "voice.status",
  "mic.taken",
  "say.outcome",
  "timer",
  "match.one",
  "match.two",
  "match.none",
  "match.doubt",
  "match.intent",
  "ask.raised",
]);

/** Events still taken once the session has ended. Judgments are not: the hand-off is settled. */
const WHILE_ENDED: ReadonlySet<SessionEventType> = new Set([
  "person.answerNow",
  "person.note",
  "person.corrected",
  "person.removed",
  "person.view",
  "person.mic",
  "person.voice",
  "voice.status",
  "mic.taken",
  "say.outcome",
  "timer",
]);

function step(s: Session, entry: TapeEntry): void {
  const { seq, at, event } = entry;
  s.lastSeq = seq;
  if (s.keys[entry.key] !== undefined) return drop(s, entry, "duplicate");
  s.keys[entry.key] = seq;

  if (s.mode === "new") {
    if (event.type !== "session.started") return drop(s, entry, "not-started");
    return start(s, entry, event);
  }
  if (event.type === "session.started") return drop(s, entry, "invalid");

  expire(s, at);
  // Start comes before the limits: the time before it was the person choosing, so it neither
  // nudges nor stops.
  if (event.type === "person.start" && (s.mode === "live" || s.mode === "paused")) {
    return begin(s, entry);
  }
  limits(s, entry);
  if ((s.mode === "finished" || s.mode === "stopped") && !WHILE_ENDED.has(event.type)) {
    return drop(s, entry, "ended");
  }
  if (s.mode === "paused" && !WHILE_PAUSED.has(event.type)) return drop(s, entry, "paused");
  if (AMENDMENTS.has(event.type) && s.mode !== "finished" && s.mode !== "stopped") {
    return drop(s, entry, "not-ended");
  }

  switch (event.type) {
    case "person.typed": {
      s.typing = false;
      s.transcript.push({
        seq,
        at,
        who: "person",
        via: "typed",
        text: event.text,
      });
      if (event.questionId === undefined) return;
      const q = find(s, event.questionId);
      if (!q) return drop(s, entry, "unknown-question");
      if (!isAnswerable(q)) return drop(s, entry, "not-answerable");
      return answer(s, entry, q, {
        input: seq,
        words: event.text,
        via: "typed",
        took: event.text,
      });
    }
    case "person.tapped": {
      const q = find(s, event.questionId);
      if (!q) return drop(s, entry, "unknown-question");
      const words = q.likely[event.option];
      if (words === undefined) return drop(s, entry, "invalid");
      if (!isAnswerable(q)) return drop(s, entry, "not-answerable");
      s.transcript.push({ seq, at, who: "person", via: "tapped", text: words });
      return answer(s, entry, q, {
        input: seq,
        words,
        via: "tapped",
        took: words,
      });
    }
    case "person.typing":
      s.typing = event.open;
      if (event.open) silence(s);
      return;
    case "person.notNow": {
      if (s.clarifier) {
        s.clarifier = null;
        silence(s);
        return;
      }
      if (!asked(s)) return drop(s, entry, "invalid");
      passAsked(s, entry, "not-now");
      speak(s, entry, { kind: "reply", text: COPY.later });
      return;
    }
    case "person.chose": {
      const c = s.clarifier;
      const fit = c?.fits.find((f) => f.questionId === event.questionId);
      const q = find(s, event.questionId);
      if (!c || !fit || !q) return drop(s, entry, "invalid");
      if (!isAnswerable(q)) return drop(s, entry, "not-answerable");
      const input = inputAt(s, c.input);
      if (!input) return drop(s, entry, "invalid");
      return answer(s, entry, q, {
        input: c.input,
        words: input.text,
        via: input.via,
        took: fit.took,
      });
    }
    case "person.picked": {
      const q = find(s, event.questionId);
      if (!q) return drop(s, entry, "unknown-question");
      if (!isAnswerable(q)) return drop(s, entry, "not-answerable");
      s.view = null;
      s.clarifier = null;
      // Picked while Abacor was asking it: still the person's pick, so what they say next answers it.
      if (s.asked === q.id) return ledger(s, seq, q, "picked");
      passAsked(s, entry, "picked-another");
      return ask(s, entry, q, "picked");
    }
    case "person.undo":
      return undo(s, entry, event.receipt);
    case "person.view":
      s.view = event.view;
      return;
    case "person.start":
      return begin(s, entry);
    case "person.pause":
      if (s.mode !== "live") return drop(s, entry, "invalid");
      s.liveMs = recordedMs(s, at);
      s.liveSince = null;
      s.mode = "paused";
      s.pausedAt = at;
      s.holding = null;
      closeUnshared(s, at);
      s.view = null;
      s.finishPrompted = false;
      s.typing = false;
      s.personSpeaking = false;
      silence(s);
      return;
    case "person.resume":
      if (s.mode !== "paused") return drop(s, entry, "invalid");
      s.mode = "live";
      s.liveSince = at;
      s.pausedAt = null;
      s.finishPrompted = false;
      return;
    case "person.finish":
      return end(s, entry, "finished", at);
    case "person.answerNow": {
      if (s.mode !== "finished") return drop(s, entry, "invalid");
      s.mode = "live";
      s.liveSince = at;
      s.endedAt = null;
      s.handoff = null;
      const first = s.questions.find((q) => q.state === "held");
      if (first) ask(s, entry, first, "answer-now");
      return;
    }
    case "person.mic": {
      s.micOn = event.on;
      // Turned on before the opening began: it no longer says how to turn it on.
      const line = current(s);
      if (event.on && line?.kind === "opening" && line.outcome === "pending") sayOpening(s, entry);
      return;
    }
    case "person.note":
      if (!onClock(s, event.moment)) return drop(s, entry, "invalid");
      s.noted.push({
        seq,
        at: event.moment,
        text: event.text,
        writtenAt: at,
        removed: false,
      });
      return;
    case "person.corrected": {
      const q = find(s, event.questionId);
      if (!q) return drop(s, entry, "unknown-question");
      if (!onClock(s, event.moment)) return drop(s, entry, "invalid");
      if ((standing(q)?.receipt ?? null) !== event.replaces) return drop(s, entry, "stale");
      return correct(s, entry, q, event.text, event.moment);
    }
    case "person.removed":
      return remove(s, entry, event.part);
    case "person.voice": {
      s.voiceMuted = event.muted;
      // Muted, the worker stops the line it was saying and reports nothing for it. The line stays
      // as the caption, unless one was waiting: with no voice there is nothing to wait for. Back
      // on, the stopped line is not resumed, so it no longer holds the next one back.
      if (event.muted) s.stoppedByMute = s.say;
      const line = current(s);
      if (event.muted ? s.next !== null : line?.outcome === "started") {
        s.say = null;
        sayNext(s);
      }
      return;
    }

    case "voice.heard":
      if (!s.micOn) return drop(s, entry, "mic-off");
      if (s.callOnMic) return drop(s, entry, "on-call");
      s.transcript.push({
        seq,
        at,
        who: "person",
        via: "voice",
        text: event.text,
      });
      return;
    case "voice.speaking":
      s.personSpeaking = event.speaking;
      if (event.speaking) gaveWay(s, entry);
      return;
    case "voice.status": {
      if (s.voice === "none") return drop(s, entry, "invalid");
      const offline = event.status === "offline";
      if (offline === (s.voice === "offline")) return;
      s.voice = event.status;
      if (offline) silence(s);
      receipt(s, entry, {
        kind: "system",
        title: offline ? COPY.voiceOffline : COPY.voiceBack,
        detail: offline ? COPY.voiceOfflineDetail : null,
      });
      return;
    }
    case "mic.taken":
      s.callOnMic = event.taken;
      if (event.taken) silence(s);
      return;
    case "say.outcome":
      return outcome(s, entry, event.intent, event.outcome, event.heardMs);

    case "screen.action":
      s.screen.push({
        seq,
        at,
        text: event.text,
        keyframe: event.keyframe ?? null,
      });
      if (event.kept === undefined || event.kept === "click") s.workedAt = at;
      if (event.kept === undefined && event.text.startsWith(MOVED_TO)) moved(s, entry, event.text);
      return;
    case "screen.masked":
      s.masked = event.masked;
      return;
    case "screen.unshared": {
      const open = s.unshared.at(-1);
      if (open && open.to === null) {
        if (open.app === event.app) return;
        open.to = at;
      }
      if (event.app !== null) {
        s.unshared.push({
          seq,
          app: event.app,
          from: at,
          to: null,
          ...(event.own ? { own: true } : {}),
        });
      }
      return;
    }

    case "break":
      s.personSpeaking = false;
      s.lastBreak = seq;
      return askNext(s, entry);
    case "timer":
      if (s.waiting !== null) askNext(s, entry);
      return;

    case "match.one": {
      const input = judgedInput(s, entry, event);
      if (!input) return;
      const q = find(s, event.questionId);
      if (!q) return drop(s, entry, "unknown-question");
      if (!isAnswerable(q)) return drop(s, entry, "not-answerable");
      const c = s.clarifier;
      const clarified =
        c && event.input > c.seq ? c.fits.find((f) => f.questionId === q.id) : undefined;
      if (c && clarified) {
        // The reply to a clarifier says which question the earlier input was about.
        const original = inputAt(s, c.input);
        if (original) {
          return answer(s, entry, q, {
            input: c.input,
            words: original.text,
            via: original.via,
            took: clarified.took,
          });
        }
      }
      if (s.asked !== q.id) talkedPast(s, entry, event.input);
      answer(s, entry, q, {
        input: event.input,
        words: input.text,
        via: input.via,
        took: event.took,
      });
      carriedOn(s, entry, event.input, !event.request);
      return requested(s, entry, event.input, event.request);
    }
    case "match.two": {
      if (!judgedInput(s, entry, event)) return;
      const fits = event.fits.map((f) => find(s, f.questionId));
      if (fits.some((q) => !q)) return drop(s, entry, "unknown-question");
      if (fits.some((q) => q && !isAnswerable(q))) return drop(s, entry, "not-answerable");
      if (!fits.some((q) => q?.id === s.asked)) talkedPast(s, entry, event.input);
      carriedOn(s, entry, event.input, false);
      s.clarifier = {
        seq,
        input: event.input,
        text: event.clarifier,
        fits: event.fits,
      };
      speak(s, entry, { kind: "clarify", text: event.clarifier });
      return;
    }
    case "match.none": {
      if (!judgedInput(s, entry, event)) return;
      if (event.request?.kind === "aside") {
        // Meant for someone else: no note, no reply, and it talks past nothing.
        const line = s.transcript.find((l) => l.seq === event.input);
        if (line?.who === "person") line.aside = true;
        return;
      }
      if (event.note && event.corrects !== undefined) {
        // Corrected again since this was judged: judge it again against the note that stands.
        const target = s.notes.find((n) => n.seq === event.corrects);
        if (!target) return drop(s, entry, "invalid");
        if (target.supersededBy !== null || target.withdrawn) return drop(s, entry, "stale");
      }
      if (event.declined) {
        const q = asked(s);
        if (q && (q.askedAt ?? 0) < event.input) passAsked(s, entry, "not-now");
      } else if (!event.request) {
        // What the person says or types to a question they picked themselves answers it, though
        // the judge read it only as something learned: they chose that question to answer it
        // (2 November 2026: a spoken answer to a picked question was passed by as talked past).
        // A correction of an earlier note is left to `addNote`, so the note it corrects is
        // superseded rather than kept beside an answer.
        const picked =
          event.note && event.corrects === undefined ? pickedFor(s, event.input) : null;
        const words = picked ? inputAt(s, event.input) : null;
        if (picked && words && event.note) {
          answer(s, entry, picked, {
            input: event.input,
            words: words.text,
            via: words.via,
            took: event.note.text,
          });
          carriedOn(s, entry, event.input, true);
          return;
        }
        // Words said to Abacor itself ("say that again?") talk past nothing; anything else does.
        talkedPast(s, entry, event.input);
      }
      if (s.clarifier && event.input > s.clarifier.seq) s.clarifier = null;
      if (event.note) addNote(s, entry, event.input, event.note, event.corrects);
      carriedOn(s, entry, event.input, !event.request);
      return requested(s, entry, event.input, event.request);
    }
    case "match.doubt":
      if (!judgedInput(s, entry, event, { pool: false })) return;
      addNote(s, entry, event.input, { topic: COPY.doubtTopic, text: event.doubt }, undefined);
      return;
    case "match.intent":
      // What the person said the work is, first or instead of what the recording had.
      if (!judgedInput(s, entry, event, { pool: false })) return;
      s.intent = event.intent;
      return;
    case "ask.raised": {
      if (!judged(s, entry, event.basedOn)) return;
      if (find(s, event.question.id)) return drop(s, entry, "invalid");
      raise(s, seq, event.question, "raised");
      return;
    }
    case "ask.cutIn": {
      if (!judged(s, entry, event.basedOn)) return;
      const q = find(s, event.questionId);
      if (!q) return drop(s, entry, "unknown-question");
      if (q.urgency !== "now" || (q.state !== "open" && q.state !== "passed")) {
        return drop(s, entry, "not-answerable");
      }
      if (!canSpeak(s) || s.typing || s.clarifier || reading(s)) return drop(s, entry, "busy");
      const shown = asked(s);
      // Displaced by Abacor, not passed by the person: it goes back as it was.
      if (shown) putBack(s, seq, shown, "replaced");
      return ask(s, entry, q, "cut-in");
    }
  }
}

function start(
  s: Session,
  entry: TapeEntry,
  event: Extract<SessionEvent, { type: "session.started" }>,
) {
  s.mode = "live";
  s.intent = event.intent;
  s.voice = event.voice;
  s.firstAt = entry.at;
  if (event.mic === "off") s.micOn = false;
  s.liveSince = entry.at;
  for (const seed of event.questions) if (!find(s, seed.id)) raise(s, entry.seq, seed, "seeded");
  s.poolChangedAt = entry.seq;
  if (s.intent === null) sayOpening(s, entry);
}

/** Abacor asks what the recording is about, with the opening's longer time to be said. */
function sayOpening(s: Session, entry: TapeEntry) {
  const text =
    s.voice === "none" ? COPY.opening : s.micOn ? COPY.openingVoiceMicOn : COPY.openingVoice;
  speak(s, entry, { kind: "opening", text, ttl: OPENING_TTL_MS });
}

/** The opening was said, or begun before the person talked over it. */
const opened = (s: Session) =>
  s.transcript.some(
    (l) =>
      l.who === "system" &&
      l.kind === "opening" &&
      (l.outcome === "started" || l.outcome === "finished" || l.outcome === "cut-off"),
  );

/**
 * The person pressed Start (ENG-1031), once: recorded time and its limits count from here, and a
 * nudge given while they chose is given again at half an hour of recording. An opening not yet
 * said is asked again from here: while they chose, the island was not in the voice room to hear it.
 */
function begin(s: Session, entry: TapeEntry) {
  if (s.startedAt !== null) return drop(s, entry, "invalid");
  s.startedAt = entry.at;
  if (entry.event.type === "person.start") s.asking = entry.event.asking ?? DEFAULT_ASKING;
  s.liveMs = 0;
  s.nudged = false;
  if (s.mode === "live") s.liveSince = entry.at;
  if (s.intent === null && !opened(s)) sayOpening(s, entry);
}

/** Works out the limits at the entry's time: the nudge, the stop and the paused prompt. */
function limits(s: Session, entry: TapeEntry) {
  const recorded = recordedMs(s, entry.at);
  if (s.mode === "live" && recorded >= STOP_MS && s.liveSince !== null) {
    // Stopped when the hour was reached, whenever the entry that noticed came.
    end(s, entry, "stopped", s.liveSince + STOP_MS - s.liveMs);
    speak(s, entry, { kind: "stopped", text: COPY.stopped });
    return;
  }
  if (s.mode === "live" && recorded >= NUDGE_MS && !s.nudged) {
    s.nudged = true;
    receipt(s, entry, {
      kind: "system",
      title: COPY.nudgeReceipt,
      detail: null,
    });
    speak(s, entry, { kind: "nudge", text: COPY.nudge, soft: true });
  }
  if (
    s.mode === "paused" &&
    s.pausedAt !== null &&
    !s.finishPrompted &&
    entry.at - s.pausedAt >= PAUSED_PROMPT_MS
  ) {
    s.finishPrompted = true;
    speak(s, entry, { kind: "finish-prompt", text: COPY.finishPrompt });
  }
}

/** Capture keeps nothing paused or ended, so a not-shared span ends there too. */
function closeUnshared(s: Session, at: number) {
  const open = s.unshared.at(-1);
  if (open && open.to === null) open.to = at;
}

function end(s: Session, entry: TapeEntry, mode: "finished" | "stopped", at: number) {
  s.liveMs = recordedMs(s, at);
  s.liveSince = null;
  s.pausedAt = null;
  s.mode = mode;
  s.endedAt = at;
  s.ended.push({ seq: entry.seq, at });
  closeUnshared(s, at);
  s.asked = null;
  s.clarifier = null;
  s.typing = false;
  s.view = null;
  silence(s);
  for (const q of s.questions) {
    if (q.state === "open" || q.state === "asked" || q.state === "passed") {
      q.state = "held";
      ledger(s, entry.seq, q, mode);
    }
  }
  s.handoff = {
    held: s.questions.filter((q) => q.state === "held").map((q) => q.id),
  };
}

/** An intent not started by its expiry is not said. */
function expire(s: Session, at: number) {
  const line = current(s);
  if (line && line.outcome === "pending" && at > line.expiresAt) {
    line.outcome = "expired";
    s.say = null;
  }
  const waiting = queued(s);
  if (waiting && at > waiting.expiresAt) {
    waiting.outcome = "expired";
    s.next = null;
  }
}

function outcome(
  s: Session,
  entry: TapeEntry,
  intent: number,
  said: "started" | "finished" | "cut-off",
  heardMs: number | undefined,
) {
  const line = s.transcript.find((l): l is SystemLine => l.who === "system" && l.seq === intent);
  if (!line) return drop(s, entry, "invalid");
  if (line.outcome === "expired") return drop(s, entry, "expired");
  if (line.outcome === "replaced" || line.outcome === "canceled")
    return drop(s, entry, "not-current");
  if (said === "started") {
    if (line.outcome !== "pending") return drop(s, entry, "invalid");
    if (entry.at > line.expiresAt) {
      line.outcome = "expired";
      if (s.say === intent) s.say = null;
      return drop(s, entry, "expired");
    }
    line.outcome = "started";
    // Its start reached the API after a mute stopped it and the voice came back on.
    if (s.say === intent && s.stoppedByMute === intent && !s.voiceMuted) {
      s.say = null;
      sayNext(s);
    }
    return;
  }
  if (line.outcome !== "pending" && line.outcome !== "started") return drop(s, entry, "invalid");
  const began = line.outcome === "started";
  line.outcome = said;
  // A barge-in: the person always wins, and the record says Abacor was cut off.
  if (said === "cut-off") {
    s.cutOff = true;
    cutShort(s, entry, line, began ? (heardMs ?? null) : 0);
  }
  if (s.say === intent) {
    s.say = null;
    sayNext(s);
  }
}

/* ------------------------------------------------------------------ */
/* Pieces of the rules                                                 */

const find = (s: Session, id: QuestionId) => s.questions.find((q) => q.id === id);
const asked = (s: Session) => s.questions.find((q) => q.id === s.asked && q.state === "asked");
const current = (s: Session) =>
  s.transcript.find((l): l is SystemLine => l.who === "system" && l.seq === s.say);
const queued = (s: Session) =>
  s.transcript.find((l): l is SystemLine => l.who === "system" && l.seq === s.next);

const canSpeak = (s: Session) => s.voice !== "offline" && !s.callOnMic && !s.typing;
/** Reading what Abacor has got is a look not to interrupt; the open questions list is not. */
const reading = (s: Session) => s.view === "understanding";
const canAsk = (s: Session) =>
  s.mode === "live" &&
  canSpeak(s) &&
  !s.clarifier &&
  !reading(s) &&
  s.holding === null &&
  !s.personSpeaking &&
  !asked(s);

function drop(s: Session, entry: TapeEntry, why: DropReason) {
  s.dropped.push({ seq: entry.seq, type: entry.event.type, why });
}

function ledger(s: Session, seq: number, q: Question, why: LedgerWhy) {
  s.ledger.push({ seq, questionId: q.id, state: q.state, why });
}

function raise(s: Session, seq: number, seed: QuestionSeed, why: "seeded" | "raised") {
  const q: Question = {
    ...seed,
    likely: [...seed.likely],
    state: "open",
    passes: 0,
    unclear: 0,
    raisedAt: seq,
    askedAt: null,
    answer: null,
    answers: [],
  };
  s.questions.push(q);
  s.poolChangedAt = seq;
  ledger(s, seq, q, why);
}

/** The person's input at `seq`: what they said or typed, or did on screen. Abacor's lines are not inputs. */
function inputAt(s: Session, seq: number): { text: string; via: Channel } | null {
  const line = s.transcript.find((l) => l.seq === seq);
  if (line?.who === "person" && line.via !== "tapped") return { text: line.text, via: line.via };
  const action = s.screen.find((a) => a.seq === seq);
  return action ? { text: action.text, via: "screen" } : null;
}

/** A judgment's position is a real earlier one, and the pool has not changed since it. */
function judged(s: Session, entry: TapeEntry, basedOn: number, { pool = true } = {}) {
  if (basedOn >= entry.seq) {
    drop(s, entry, "invalid");
    return false;
  }
  if (pool && basedOn < s.poolChangedAt) {
    drop(s, entry, "stale");
    return false;
  }
  return true;
}

function judgedInput(
  s: Session,
  entry: TapeEntry,
  event: { input: number; basedOn: number },
  options: { pool?: boolean } = {},
) {
  if (event.input > event.basedOn) {
    drop(s, entry, "invalid");
    return null;
  }
  const input = inputAt(s, event.input);
  if (!input) {
    drop(s, entry, "not-person");
    return null;
  }
  return judged(s, entry, event.basedOn, options) ? input : null;
}

/**
 * The question showing, when the person picked it themselves from the open list and `input` is
 * the first thing they said or typed since: that input is their answer to it.
 */
function pickedFor(s: Session, input: number): Question | null {
  const q = asked(s);
  if (!q || (q.askedAt ?? 0) >= input) return null;
  const last = s.ledger.findLast((l) => l.questionId === q.id);
  if (last?.why !== "picked") return null;
  const said = s.transcript.find((l) => l.seq === input);
  if (said?.who !== "person" || said.via === "tapped") return null;
  const between = s.transcript.some(
    (l) => l.who === "person" && l.seq > (q.askedAt ?? 0) && l.seq < input && !l.aside,
  );
  return between ? null : q;
}

/** The person carried on after the question showing was asked: one pass. */
function talkedPast(s: Session, entry: TapeEntry, input: number) {
  const q = asked(s);
  if (!q || (q.askedAt ?? 0) >= input) return;
  // Working on screen while Abacor is still saying the question does not pass it by.
  const saying = current(s);
  const onScreen = s.screen.some((a) => a.seq === input);
  if (onScreen && saying?.questionId === q.id && saying.outcome === "started") return;
  passAsked(s, entry, "talked-past");
}

function passAsked(
  s: Session,
  entry: TapeEntry,
  why: "talked-past" | "not-now" | "picked-another",
) {
  const q = asked(s);
  if (!q) return;
  q.passes += 1;
  q.state = q.passes >= 2 ? "held" : "passed";
  s.asked = null;
  unsay(s, q.id);
  ledger(s, entry.seq, q, why);
  if (q.state === "held")
    receipt(s, entry, {
      kind: "held",
      title: q.text,
      detail: COPY.held,
      questionId: q.id,
    });
}

/** A question goes back to the pool as it was, uncounted: the person did not pass it by. */
function putBack(s: Session, seq: number, q: Question, why: "replaced" | "gave-way" | "cut-short") {
  q.state = q.passes > 0 ? "passed" : "open";
  s.asked = null;
  ledger(s, seq, q, why);
}

/** Abacor's question lines, which give way to the person rather than talk over them. */
const QUESTION_KINDS: ReadonlySet<SayKind> = new Set(["ask", "retry"]);

/** The person chose the question showing themselves: what they say next is their answer to it. */
const chosen = (s: Session, q: Question) => {
  const why = s.ledger.findLast((l) => l.questionId === q.id)?.why;
  return why === "picked" || why === "answer-now";
};

/**
 * The person started speaking before Abacor began its question (3 November 2026: they went on
 * within a second of half their pauses, and questions began over them): it is not said now, and
 * it is asked again at the next pause, unheard and so not passed by. A question they picked stays
 * asked: they are answering it, so only its words are dropped.
 */
function gaveWay(s: Session, entry: TapeEntry) {
  const line = current(s);
  const q = asked(s);
  if (!line || !q || line.outcome !== "pending" || !QUESTION_KINDS.has(line.kind)) return;
  if (line.questionId !== q.id) return;
  if (chosen(s, q)) {
    line.outcome = "canceled";
    s.say = null;
    return sayNext(s);
  }
  putBack(s, entry.seq, q, "gave-way");
  s.again = q.id;
  unsay(s, q.id);
}

/**
 * A question the person cut off after it had played less than `CUT_SHORT_MS` (`heardMs`, as the
 * worker measured it; 0 when it never began) was not heard: it is asked again at the next pause,
 * once. With no measure (a worker before it), cut off again, cut off later, or one they picked,
 * it stays asked, and what they go on to say answers it or passes it by.
 */
function cutShort(s: Session, entry: TapeEntry, line: SystemLine, heardMs: number | null) {
  const q = asked(s);
  if (!q || line.questionId !== q.id || !QUESTION_KINDS.has(line.kind) || chosen(s, q)) return;
  if (heardMs === null || heardMs >= CUT_SHORT_MS) return;
  if (s.ledger.some((l) => l.questionId === q.id && l.why === "cut-short")) return;
  putBack(s, entry.seq, q, "cut-short");
  s.again = q.id;
}

/** How capture words a move to another app, window or page (`capture-service.ts`). */
const MOVED_TO = "Moved to ";

/**
 * The person moved to the page `place`. One not visited for `STEP_NEW_PAGE_MS` ends a step: going
 * back and forth between two windows (the order email and the invoice) is one step, and reaching
 * the next page (the saved invoice, the list again) ends it. In `step-end` a held question is asked
 * there, when Abacor can.
 */
function moved(s: Session, entry: TapeEntry, place: string) {
  const last = s.pages[place];
  s.pages[place] = entry.at;
  if (last !== undefined && entry.at - last <= STEP_NEW_PAGE_MS) return;
  s.stepEnded = { seq: entry.seq, at: entry.at };
  if (s.asking === "step-end" && s.waiting !== null) askNext(s, entry);
}

/** Whether the person did something on screen in the `WORKING_MS` before `at`. */
function working(s: Session, at: number): boolean {
  const last = s.screen.findLast((a) => !a.removed);
  return last !== undefined && last.at <= at && at - last.at < WORKING_MS;
}

/**
 * At a pause, Abacor asks: an "about now" question first, then the newest, then the one retry.
 * While the person is still working on screen only an "about now" question is asked; the others
 * wait until the work pauses too.
 */
function askNext(s: Session, entry: TapeEntry) {
  // Abacor can't ask now (a question is showing, the person is typing or speaking): a held pause
  // is let go rather than kept due, so its deadline never stays in the past (`dueAt`); the next
  // break asks as it always did.
  if (!canAsk(s)) {
    s.waiting = null;
    s.waitingSince = null;
    return;
  }
  // A question the person never got to hear was already due: it goes first, whatever the mode.
  const again = s.again === null ? undefined : find(s, s.again);
  s.again = null;
  if (again && (again.state === "open" || again.state === "passed")) {
    return ask(s, entry, again, again.state === "passed" ? "retried" : "asked");
  }
  if (s.asking === "step-end") return askAtStepEnd(s, entry);
  const pool = s.questions.filter((q) => q.state === "open");
  const now = pool.findLast((q) => q.urgency === "now");
  const held = s.waitingSince !== null && entry.at - s.waitingSince >= MAX_HOLD_MS;
  if (now === undefined && working(s, entry.at) && !held) {
    s.waiting = entry.seq;
    s.waitingSince ??= entry.at;
    return;
  }
  s.waiting = null;
  s.waitingSince = null;
  const next = now ?? pool.at(-1) ?? s.questions.find((q) => q.state === "passed");
  if (next) ask(s, entry, next, next.state === "passed" ? "retried" : "asked");
}

/**
 * `askNext` in `step-end`: an "about now" question at once; one raised before the last end of a step,
 * or a retry asked before it, at a pause within `STEP_ASK_WITHIN_MS` of it; any after `STEP_HOLD_MS`
 * of holding. Otherwise it holds, while there is something to hold.
 */
function askAtStepEnd(s: Session, entry: TapeEntry) {
  const open = s.questions.filter((q) => q.state === "open");
  const passed = s.questions.find((q) => q.state === "passed");
  const step = s.stepEnded;
  const near = step !== null && entry.at - step.at <= STEP_ASK_WITHIN_MS;
  const late = s.waitingSince !== null && entry.at - s.waitingSince >= STEP_HOLD_MS;
  const next =
    open.findLast((q) => q.urgency === "now") ??
    (near && step ? open.findLast((q) => q.raisedAt < step.seq) : undefined) ??
    (near && step && passed && (passed.askedAt ?? 0) < step.seq ? passed : undefined) ??
    (late ? (open.at(-1) ?? passed) : undefined);
  if (next) {
    s.waiting = null;
    s.waitingSince = null;
    return ask(s, entry, next, next.state === "passed" ? "retried" : "asked");
  }
  if (open.length > 0 || passed) {
    s.waiting = entry.seq;
    s.waitingSince ??= entry.at;
  } else {
    s.waiting = null;
    s.waitingSince = null;
  }
}

/**
 * What the person said or typed (`input`), judged as not an aside, ends a hold it came after. The pause after it may
 * already be on the tape (judging takes a moment), and then Abacor asks as that pause would have;
 * `ask` false when something else is said instead (a reply, a clarifier).
 */
function carriedOn(s: Session, entry: TapeEntry, input: number, ask: boolean) {
  if (s.holding === null || input <= s.holding) return;
  // Only what the person says or types carries on; working on screen is not done holding.
  if (!s.transcript.some((l) => l.who === "person" && l.seq === input)) return;
  s.holding = null;
  if (ask && s.lastBreak !== null && s.lastBreak > input) askNext(s, entry);
}

/** Abacor replies to what the input `input` asked of it (see Replies, above). */
function requested(s: Session, entry: TapeEntry, input: number, request: Request | undefined) {
  if (!request || request.kind === "aside" || s.mode !== "live") return;
  // A question asked at the break after the input gives way: what the person asked comes first.
  // One Abacor has started saying is finished, and the reply follows it.
  const after = asked(s);
  const saying = current(s);
  const started = saying?.questionId === after?.id && saying?.outcome === "started";
  if (after && (after.askedAt ?? 0) > input && !started) {
    putBack(s, entry.seq, after, "replaced");
    unsay(s, after.id);
  }
  const reply = (text: string, questionId?: QuestionId) =>
    speak(s, entry, {
      kind: "reply",
      text,
      ...(questionId ? { questionId } : {}),
    });
  switch (request.kind) {
    case "status":
      return reply(statusLine(s, request.about));
    case "recall":
      return reply(request.answer);
    case "judge":
      // The doubt is kept by its own entry (`match.doubt`).
      return reply(COPY.judge);
    case "recap": {
      const count = understanding(s).length;
      if (count > 0) s.view = "understanding";
      return reply(count > 0 ? COPY.recap(count) : COPY.recapNone);
    }
    case "repeat": {
      // Nothing to repeat: the request goes unanswered, and the rest of the judgment stands.
      const line = lastSaid(s, input);
      if (!line) return;
      return reply(line.text, line.questionId ?? undefined);
    }
    case "hold":
      s.holding = entry.seq;
      return reply(COPY.hold);
    case "clarify": {
      // The question is gone (this input answered it) or came after: nothing to explain.
      const q = asked(s);
      if (!q || (q.askedAt ?? 0) > input) return;
      q.unclear += 1;
      if (q.unclear < 2) return reply(`${request.meaning} ${q.text}`, q.id);
      // Asked twice what it means: worded in Abacor's terms, not theirs. The chat asks it better.
      q.state = "held";
      s.asked = null;
      unsay(s, q.id);
      ledger(s, entry.seq, q, "unclear");
      receipt(s, entry, {
        kind: "held",
        title: q.text,
        detail: COPY.held,
        questionId: q.id,
      });
      return reply(COPY.unclear);
    }
  }
}

/** About the session itself, from what the session knows, never from what a model wrote. */
function statusLine(s: Session, about: "screen" | "hearing" | "next"): string {
  if (about === "hearing") return s.micOn ? COPY.hearing : COPY.hearingOff;
  if (about === "next") return COPY.next;
  if (s.masked) return COPY.seeingMasked;
  const away = s.unshared.find((u) => u.to === null);
  if (away) return away.own ? COPY.seeingOwn : COPY.seeingUnshared(away.app);
  // Capture has said where the person is, a screen or a move in or out of what is shared: it is
  // on, though a screen may not have come through yet.
  return s.screen.length > 0 || s.unshared.length > 0 ? COPY.seeing : COPY.seeingNothing;
}

/**
 * What "say that again" repeats: the question showing when the person spoke, or else the last line
 * Abacor said before it. A "got it" is not what anyone asks to hear again.
 */
function lastSaid(s: Session, input: number): SystemLine | null {
  const said = s.transcript.filter(
    (l): l is SystemLine =>
      l.who === "system" &&
      l.seq < input &&
      l.kind !== "receipt" &&
      (l.outcome === "started" || l.outcome === "finished" || l.outcome === "cut-off"),
  );
  const q = asked(s);
  const showing =
    q && (q.askedAt ?? 0) < input ? said.findLast((l) => l.questionId === q.id) : null;
  return showing ?? said.at(-1) ?? null;
}

function ask(
  s: Session,
  entry: TapeEntry,
  q: Question,
  why: "asked" | "retried" | "picked" | "cut-in" | "answer-now",
) {
  q.state = "asked";
  q.askedAt = entry.seq;
  s.asked = q.id;
  // Whatever asked it (a pause, a pick, a cut-in), no held pause is still waiting to ask.
  s.waiting = null;
  s.waitingSince = null;
  // The question asked takes the open questions list's place.
  if (s.view === "open") s.view = null;
  ledger(s, entry.seq, q, why);
  if (why === "cut-in") {
    speak(s, entry, {
      kind: "cut-in",
      text: q.text,
      cue: COPY.cue,
      questionId: q.id,
      ttl: CUT_IN_TTL_MS,
    });
  } else if (why === "retried") {
    speak(s, entry, {
      kind: "retry",
      text: COPY.retry(q.text),
      questionId: q.id,
    });
  } else speak(s, entry, { kind: "ask", text: q.text, questionId: q.id });
}

type Answer = NonNullable<Question["answer"]>;

function answer(s: Session, entry: TapeEntry, q: Question, a: Omit<Answer, "receipt">) {
  // Covered along the way before Abacor ever asked it: Abacor closes it, and the person can reopen it.
  const along =
    q.state === "open" && (a.via === "voice" || a.via === "screen") && q.askedAt === null;
  q.state = along ? "closed" : "answered";
  q.answer = { ...a, receipt: entry.seq };
  q.answers.push({ ...q.answer, at: entry.at, undone: false });
  if (s.asked === q.id) s.asked = null;
  s.clarifier = null;
  s.poolChangedAt = entry.seq;
  unsay(s, q.id);
  ledger(s, entry.seq, q, along ? "closed" : "answered");
  if (along) {
    receipt(s, entry, {
      kind: "closed",
      title: q.text,
      detail: a.words,
      action: "reopen",
      questionId: q.id,
    });
  } else {
    receipt(s, entry, {
      kind: "answered",
      title: a.took,
      detail: a.words,
      action: "undo",
      questionId: q.id,
    });
    speak(s, entry, { kind: "receipt", text: COPY.gotIt, soft: true });
  }
}

function addNote(
  s: Session,
  entry: TapeEntry,
  input: number,
  note: { topic: string; text: string; covers?: Cover[] | undefined },
  corrects: number | undefined,
) {
  const earlier = s.notes.find(
    (n) => n.seq === corrects && n.supersededBy === null && !n.withdrawn,
  );
  s.notes.push({
    seq: entry.seq,
    input,
    topic: note.topic,
    text: note.text,
    covers: note.covers ? note.covers.map((c) => ({ ...c })) : [],
    supersedes: earlier?.seq ?? null,
    supersededBy: null,
    withdrawn: false,
  });
  if (earlier) {
    // The latest statement wins; both stay, linked.
    earlier.supersededBy = entry.seq;
    receipt(s, entry, {
      kind: "changed",
      title: note.text,
      detail: `Was: ${earlier.text}`,
      action: "undo",
      note: entry.seq,
    });
  } else
    receipt(s, entry, {
      kind: "understood",
      title: note.text,
      detail: null,
      note: entry.seq,
    });
}

function undo(s: Session, entry: TapeEntry, seq: number) {
  // An entry may also make a held or system receipt, but never more than one with an action.
  const r = s.receipts.find((x) => x.seq === seq && x.action !== null);
  if (!r?.action || r.reversed) return drop(s, entry, "invalid");
  const q = r.questionId === null ? undefined : find(s, r.questionId);
  if (q) {
    if (q.answer?.receipt !== r.seq) return drop(s, entry, "not-current");
    q.state = "open";
    q.answer = null;
    const given = q.answers.find((x) => x.receipt === r.seq);
    if (given) given.undone = true;
    s.poolChangedAt = entry.seq;
    ledger(s, entry.seq, q, r.action === "reopen" ? "reopened" : "undone");
  }
  const note = s.notes.find((n) => n.seq === r.note);
  if (note) {
    if (note.withdrawn || note.supersededBy !== null) return drop(s, entry, "not-current");
    note.withdrawn = true;
    const earlier = s.notes.find((n) => n.seq === note.supersedes);
    if (earlier) earlier.supersededBy = null;
  }
  r.reversed = true;
}

/** The amendments: taken only once the session has ended. */
const AMENDMENTS: ReadonlySet<SessionEventType> = new Set([
  "person.note",
  "person.corrected",
  "person.removed",
]);

/** A moment on the recording's clock: from Start (or the first entry) to the end. */
const onClock = (s: Session, moment: number) =>
  moment >= (s.startedAt ?? s.firstAt ?? 0) && s.endedAt !== null && moment <= s.endedAt;

/** The answer that stands: the newest not undone and not removed. */
const standing = (q: Question) => q.answers.findLast((a) => !a.undone && !a.removed) ?? null;

/** The questions held for the chat, again, after an amendment answered or unanswered one. */
function settleHandoff(s: Session) {
  if (s.handoff)
    s.handoff = {
      held: s.questions.filter((q) => q.state === "held").map((q) => q.id),
    };
}

/**
 * After notes were withdrawn, each note that stands corrected only by withdrawn ones stands again:
 * its `supersededBy` moves on to the next correction that still stands, or clears. So with A
 * corrected by B and B by C, withdrawing B and C brings back A.
 */
function relinkNotes(s: Session) {
  const bySeq = new Map(s.notes.map((n) => [n.seq, n]));
  for (const note of s.notes) {
    let next = note.supersededBy === null ? undefined : bySeq.get(note.supersededBy);
    while (next?.withdrawn)
      next = next.supersededBy === null ? undefined : bySeq.get(next.supersededBy);
    note.supersededBy = next?.seq ?? null;
  }
}

/**
 * After answers dropped out, an answer that was corrected points at the next correction that is
 * still there, or at none, so the one that stands again reads as standing.
 */
function relinkCorrections(q: Question) {
  q.answers.forEach((a, i) => {
    if (a.correctedBy === undefined) return;
    const next = q.answers.slice(i + 1).find((b) => b.moment !== undefined && !b.removed);
    if (next) a.correctedBy = next.receipt;
    else delete a.correctedBy;
  });
}

/** The person's own words, given again for `q` after the end: they stand, and the earlier stays. */
function correct(s: Session, entry: TapeEntry, q: Question, text: string, moment: number) {
  const earlier = standing(q);
  if (earlier) earlier.correctedBy = entry.seq;
  q.answer = {
    input: entry.seq,
    words: text,
    via: "typed",
    took: text,
    receipt: entry.seq,
  };
  q.answers.push({
    ...q.answer,
    at: entry.at,
    undone: false,
    moment,
    ...(earlier ? { corrects: earlier.receipt } : {}),
  });
  q.state = "answered";
  ledger(s, entry.seq, q, "corrected");
  settleHandoff(s);
}

/**
 * Takes out a part, or everything in a stretch, and what Abacor took from it: the notes made from
 * a removed input are withdrawn (the one a withdrawn correction replaced stands again), and an
 * answer given by one drops out, so the answer before it stands, or the question is held.
 */
function remove(s: Session, entry: TapeEntry, part: RemovedPart) {
  const inputs = new Set<number>();
  const person = (seq: number) =>
    s.transcript.find((l): l is PersonLine => l.who === "person" && l.seq === seq && !l.removed);
  const noted = (seq: number) => s.noted.find((n) => n.seq === seq && !n.removed);
  switch (part.kind) {
    case "said": {
      const line = person(part.seq);
      const note = noted(part.seq);
      if (!line && !note) return drop(s, entry, "invalid");
      if (line) line.removed = true;
      if (note) note.removed = true;
      inputs.add(part.seq);
      break;
    }
    case "screen":
    case "action": {
      if (part.entry === null) break;
      const action = s.screen.find((a) => a.seq === part.entry && !a.removed);
      if (!action || (part.kind === "screen" && action.keyframe !== part.id)) {
        return drop(s, entry, "invalid");
      }
      action.removed = true;
      inputs.add(action.seq);
      break;
    }
    case "range": {
      const within = (at: number) => at >= part.from && at <= part.to;
      for (const line of s.transcript) {
        if (line.who === "person" && !line.removed && within(line.at)) {
          line.removed = true;
          inputs.add(line.seq);
        }
      }
      // Screens and actions by the entries the API found by their capture time, not by when
      // their entries landed.
      const captured = new Set(part.entries);
      for (const action of s.screen) {
        if (!action.removed && captured.has(action.seq)) {
          action.removed = true;
          inputs.add(action.seq);
        }
      }
      for (const note of s.noted) if (!note.removed && within(note.at)) note.removed = true;
      // A correction placed in the stretch goes with it: its input is its own entry.
      for (const q of s.questions) {
        for (const a of q.answers)
          if (a.moment !== undefined && within(a.moment)) inputs.add(a.input);
      }
      break;
    }
  }
  const dropped: Removal["dropped"] = { notes: [], answers: [] };
  for (const note of s.notes) {
    if (!inputs.has(note.input) || note.withdrawn) continue;
    note.withdrawn = true;
    dropped.notes.push(note.seq);
  }
  relinkNotes(s);
  for (const q of s.questions) {
    let changed = false;
    for (const a of q.answers) {
      if (a.removed || !inputs.has(a.input)) continue;
      a.removed = true;
      changed = true;
      dropped.answers.push({ questionId: q.id, receipt: a.receipt });
    }
    if (!changed) continue;
    relinkCorrections(q);
    const stands = standing(q);
    q.answer = stands
      ? {
          input: stands.input,
          words: stands.words,
          via: stands.via,
          took: stands.took,
          receipt: stands.receipt,
        }
      : null;
    if (!stands && (q.state === "answered" || q.state === "closed")) q.state = "held";
    ledger(s, entry.seq, q, "removed");
  }
  s.removals.push({ seq: entry.seq, at: entry.at, part, dropped });
  settleHandoff(s);
}

function receipt(
  s: Session,
  entry: TapeEntry,
  r: Pick<Receipt, "kind" | "title" | "detail"> &
    Partial<Pick<Receipt, "action" | "questionId" | "note">>,
) {
  s.receipts.push({
    seq: entry.seq,
    at: entry.at,
    action: null,
    questionId: null,
    note: null,
    ...r,
    reversed: false,
  });
}

/**
 * Makes a say intent, unless Abacor cannot speak now. A soft one (a receipt, a nudge) never talks
 * over a line already being said. Nor does Abacor cut itself off: a line it makes on its own (a
 * reply, a question at a break) while another is being said waits as `next`, the newest in place
 * of an older one; what the person did (a pick, Not now) is answered at once. One entry makes at
 * most one intent; a later one replaces it.
 */
function speak(
  s: Session,
  entry: TapeEntry,
  line: {
    kind: SayKind;
    text: string;
    cue?: string;
    questionId?: QuestionId;
    ttl?: number;
    soft?: boolean;
  },
) {
  if (!canSpeak(s)) return;
  // Paused or ended, Abacor says only the finish prompt and the stop.
  if (s.mode !== "live" && line.kind !== "finish-prompt" && line.kind !== "stopped") return;
  const now = current(s);
  if (line.soft && (s.personSpeaking || now?.outcome === "started")) return;
  // Muted, the line is a caption and nothing is being said aloud: the newer one shows at once.
  const waits =
    now?.outcome === "started" && !s.voiceMuted && !entry.event.type.startsWith("person.");
  const older = queued(s);
  if (older && older.seq !== entry.seq) older.outcome = "replaced";
  s.next = null;
  if (!waits && now?.outcome === "pending") now.outcome = "replaced";
  s.transcript = s.transcript.filter((l) => !(l.who === "system" && l.seq === entry.seq));
  s.transcript.push({
    seq: entry.seq,
    at: entry.at,
    who: "system",
    kind: line.kind,
    text: line.text,
    cue: line.cue ?? null,
    questionId: line.questionId ?? null,
    // A line that waits may wait as long as the longest line takes to say.
    expiresAt: entry.at + (line.ttl ?? SAY_INTENT_TTL_MS) + (waits ? SAY_WAIT_MS : 0),
    outcome: "pending",
  });
  if (waits) {
    s.next = entry.seq;
    return;
  }
  s.say = entry.seq;
  s.cutOff = false;
}

/** The line that waited becomes the one to say. */
function sayNext(s: Session) {
  const line = queued(s);
  s.next = null;
  if (line?.outcome !== "pending") return;
  s.say = line.seq;
  s.cutOff = false;
}

/** Abacor stops: the current intent, if not yet started, will not be said, nor the one waiting. */
function silence(s: Session) {
  const line = current(s);
  if (line?.outcome === "pending") line.outcome = "canceled";
  s.say = null;
  const waiting = queued(s);
  if (waiting?.outcome === "pending") waiting.outcome = "canceled";
  s.next = null;
}

/** A question went away: Abacor stops saying it, or will not, and says what waited instead. */
function unsay(s: Session, id: QuestionId) {
  const line = current(s);
  if (line?.questionId === id) {
    if (line.outcome === "pending") line.outcome = "canceled";
    s.say = null;
    return sayNext(s);
  }
  const waiting = queued(s);
  if (waiting?.questionId === id) {
    waiting.outcome = "canceled";
    s.next = null;
  }
}
